import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useGetBlogPostBySlugQuery } from "../../services/blogApi";
import "./BlogDetail.css";

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    // RTK Query hook
    const {
        data: postData,
        isLoading,
        error,
        refetch
    } = useGetBlogPostBySlugQuery(slug, {
        skip: !slug, // Slug yoksa query'yi çalıştırma
    });

    // Verileri al
    const post = postData?.success ? postData.data.post : null;
    const relatedPosts = postData?.success ? postData.data.related_posts || [] : [];

    // Sayfa yüklendiğinde scroll'u en üste al
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    // Markdown'ı basit HTML'e çevir (gerçek projede react-markdown kullanılabilir)
    const renderContent = (content, contentType = 'html') => {
        if (contentType === 'markdown') {
            return content
                .replace(/^# (.*$)/gim, '<h1 id="$1">$1</h1>')
                .replace(/^## (.*$)/gim, '<h2 id="$1">$1</h2>')
                .replace(/^### (.*$)/gim, '<h3 id="$1">$1</h3>')
                .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
                .replace(/\*(.*)\*/gim, '<em>$1</em>')
                .replace(/^\- (.*$)/gim, '<li>$1</li>')
                .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
                .replace(/\n\n/gim, '</p><p>')
                .replace(/^(?!<[h|l|p])/gim, '<p>')
                .replace(/(?![h|l|p]>)$/gim, '</p>')
                .replace(/<li>/gim, '<ul><li>')
                .replace(/<\/li>/gim, '</li></ul>')
                .replace(/<\/ul><ul>/gim, '');
        }
        return content; // HTML olarak döndür
    };

    const sharePost = (platform) => {
        const url = window.location.href;
        const title = post.title;

        let shareUrl = '';

        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
                break;
            case 'whatsapp':
                shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
                break;
        }

        window.open(shareUrl, '_blank', 'width=600,height=400');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // İçindekiler tablosunu oluştur
    const generateTableOfContents = (content) => {
        if (!content) return [];

        const headings = [];
        const headingRegex = /<h([1-6])[^>]*>(.*?)<\/h[1-6]>/gi;
        let match;

        while ((match = headingRegex.exec(content)) !== null) {
            const level = parseInt(match[1]);
            const text = match[2].replace(/<[^>]*>/g, ''); // HTML etiketlerini temizle
            const id = text.toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .trim();

            headings.push({ level, text, id });
        }

        return headings;
    };

    // Loading state
    if (isLoading) {
        return (
            <>
                <Helmet>
                    <title>Yükleniyor... | BiKuyum Blog</title>
                </Helmet>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                </div>
            </>
        );
    }

    // Error state
    if (error || !post) {
        const errorMessage = error?.data?.error || error?.message || 'Blog yazısı bulunamadı';

        return (
            <>
                <Helmet>
                    <title>Sayfa Bulunamadı | BiKuyum Blog</title>
                    <meta name="description" content="Aradığınız blog yazısı bulunamadı. BiKuyum blog sayfasına dönebilirsiniz." />
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <div className="container py-5 text-center">
                    <h1>Blog yazısı bulunamadı</h1>
                    <p className="text-muted">{errorMessage}</p>
                    <div className="d-flex gap-2 justify-content-center">
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/blog')}
                        >
                            Blog'a Dön
                        </button>
                        {error && (
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => refetch()}
                            >
                                <i className="fas fa-redo me-1"></i>
                                Tekrar Dene
                            </button>
                        )}
                    </div>
                </div>
            </>
        );
    }

    const tableOfContents = generateTableOfContents(renderContent(post.content, post.content_type));

    return (
        <>
            <Helmet>
                <title>{post.title} | BiKuyum Blog</title>
                <meta name="description" content={post.meta_description || post.excerpt} />
                <meta name="keywords" content={post.meta_keywords || post.tags.join(', ')} />
                <meta name="author" content="BiKuyum Uzmanı" />
                <meta name="robots" content="index, follow" />

                {/* Open Graph */}
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.meta_description || post.excerpt} />
                <meta property="og:image" content={post.image_url} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://bikuyum.com/blog/${post.slug}`} />
                <meta property="og:site_name" content="BiKuyum" />

                {/* Article specific */}
                <meta property="article:author" content="BiKuyum Uzmanı" />
                <meta property="article:published_time" content={post.published_date} />
                <meta property="article:modified_time" content={post.updated_date || post.published_date} />
                <meta property="article:section" content={post.category} />
                {post.tags.map((tag, index) => (
                    <meta key={index} property="article:tag" content={tag} />
                ))}

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.meta_description || post.excerpt} />
                <meta name="twitter:image" content={post.image_url} />

                <link rel="canonical" href={`https://bikuyum.com/blog/${post.slug}`} />

                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": post.title,
                        "description": post.meta_description || post.excerpt,
                        "image": {
                            "@type": "ImageObject",
                            "url": post.image_url,
                            "width": 1200,
                            "height": 630
                        },
                        "author": {
                            "@type": "Person",
                            "name": "BiKuyum Uzmanı",
                            "url": "https://bikuyum.com"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "BiKuyum",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://bikuyum.com/logo.png",
                                "width": 200,
                                "height": 60
                            },
                            "url": "https://bikuyum.com"
                        },
                        "datePublished": post.published_date,
                        "dateModified": post.updated_date || post.published_date,
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `https://bikuyum.com/blog/${post.slug}`
                        },
                        "keywords": post.tags.join(', '),
                        "wordCount": post.content ? post.content.split(' ').length : 0,
                        "timeRequired": `PT${post.reading_time}M`,
                        "inLanguage": "tr-TR",
                        "isAccessibleForFree": true,
                        "genre": post.category,
                        "about": {
                            "@type": "Thing",
                            "name": "Altın ve Mücevher"
                        }
                    })}
                </script>

                {/* FAQ Schema (eğer içerikte SSS varsa) */}
                {tableOfContents.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            "mainEntity": tableOfContents.map(heading => ({
                                "@type": "Question",
                                "name": heading.text,
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": `${heading.text} hakkında detaylı bilgi için blog yazımızı okuyun.`
                                }
                            }))
                        })}
                    </script>
                )}
            </Helmet>

            <div className="blog-detail-section row px-2">
                {/* Breadcrumb */}
                <div className="container pt-3">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb breadcrumb-custom" itemScope itemType="https://schema.org/BreadcrumbList">
                            <li className="breadcrumb-item" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                                <Link to="/" itemProp="item">
                                    <span itemProp="name">Ana Sayfa</span>
                                </Link>
                                <meta itemProp="position" content="1" />
                            </li>
                            <li className="breadcrumb-item" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                                <Link to="/blog" itemProp="item">
                                    <span itemProp="name">Blog</span>
                                </Link>
                                <meta itemProp="position" content="2" />
                            </li>
                            <li className="breadcrumb-item active" aria-current="page" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                                <span itemProp="name">{post.title}</span>
                                <meta itemProp="position" content="3" />
                            </li>
                        </ol>
                    </nav>
                </div>

                {/* Header */}
                <header className="blog-detail-header col-lg-12">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8 mx-auto text-center">
                                <h1 className="display-5 fw-bold mb-4">{post.title}</h1>

                                <div className="blog-meta-detail">
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-center justify-content-md-center justify-content-start">
                                                <div className="author-avatar bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{ width: '40px', height: '40px' }}>
                                                    <i className="fas fa-user text-white"></i>
                                                </div>
                                                <div className="d-flex flex-column align-items-start">
                                                    <div className="fw-bold">BiKuyum Uzmanı</div>
                                                    <time className="text-muted small" dateTime={post.published_date}>
                                                        {formatDate(post.published_date)}
                                                    </time>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mt-3 mt-md-0">
                                            <div className="d-flex align-items-center justify-content-center justify-content-md-end">
                                                <span className="badge bg-primary me-2">
                                                    <i className="far fa-clock me-1"></i>
                                                    {post.reading_time} dk okuma
                                                </span>
                                                {post.view_count > 0 && (
                                                    <span className="badge bg-secondary me-2">
                                                        <i className="far fa-eye me-1"></i>
                                                        {post.view_count} görüntülenme
                                                    </span>
                                                )}
                                                {post.tags && post.tags.slice(0, 2).map((tag, index) => (
                                                    <span key={index} className="badge bg-secondary me-1">
                                                        #{tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="container py-5 px-0">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="d-flex flex-md-row flex-column gap-4">
                                {/* Table of Contents */}
                                {tableOfContents.length > 0 && (
                                    <nav className="table-of-contents" aria-label="İçindekiler">
                                        <h2 className="h4 ms-3"><i className="fas fa-list me-2"></i>İçindekiler</h2>
                                        <ol className="">
                                            {tableOfContents.map((heading, index) => (
                                                <li key={index}>
                                                    <a href={`#${heading.id}`}>{heading.text}</a>
                                                </li>
                                            ))}
                                        </ol>
                                    </nav>
                                )}

                                {/* Featured Image */}
                                <figure className="mb-0 w-100">
                                    <img
                                        src={post.image_url}
                                        alt={post.title}
                                        className="blog-detail-image w-100"
                                        style={{ height: '400px', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.style.backgroundColor = '#e9ecef';
                                            e.target.style.display = 'flex';
                                            e.target.style.alignItems = 'center';
                                            e.target.style.justifyContent = 'center';
                                            e.target.innerHTML = '<i class="fas fa-image text-muted fa-3x"></i>';
                                        }}
                                    />
                                </figure>
                            </div>

                            {/* Blog Content */}
                            <article
                                className="blog-content"
                                dangerouslySetInnerHTML={{
                                    __html: renderContent(post.content, post.content_type)
                                }}
                            />

                            {/* Tags */}
                            {post.tags && post.tags.length > 0 && (
                                <div className="mt-4 pt-3 border-top">
                                    <h3 className="h6 mb-2">Etiketler:</h3>
                                    <div className="d-flex flex-wrap gap-2">
                                        {post.tags.map((tag, index) => (
                                            <span key={index} className="badge bg-light text-dark border">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Share Buttons */}
                            <div className="mt-5 pt-4 border-top">
                                <h3 className="h5 mb-3 text-center">Bu yazıyı paylaş:</h3>
                                <div className="share-buttons">
                                    <button
                                        className="share-btn facebook"
                                        onClick={() => sharePost('facebook')}
                                        aria-label="Facebook'ta paylaş"
                                    >
                                        <i className="fab fa-facebook-f"></i>
                                        Facebook
                                    </button>
                                    <button
                                        className="share-btn twitter"
                                        onClick={() => sharePost('twitter')}
                                        aria-label="Twitter'da paylaş"
                                    >
                                        <i className="fab fa-twitter"></i>
                                        Twitter
                                    </button>
                                    <button
                                        className="share-btn linkedin"
                                        onClick={() => sharePost('linkedin')}
                                        aria-label="LinkedIn'de paylaş"
                                    >
                                        <i className="fab fa-linkedin-in"></i>
                                        LinkedIn
                                    </button>
                                    <button
                                        className="share-btn whatsapp"
                                        onClick={() => sharePost('whatsapp')}
                                        aria-label="WhatsApp'ta paylaş"
                                    >
                                        <i className="fab fa-whatsapp"></i>
                                        WhatsApp
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Related Posts */}
                    {relatedPosts.length > 0 && (
                        <aside className="related-posts">
                            <div className="row">
                                <div className="col-12">
                                    <h2 className="h3 mb-4 text-center">İlgili Yazılar</h2>
                                </div>
                            </div>
                            <div className="row g-4">
                                {relatedPosts.map((relatedPost) => (
                                    <div key={relatedPost.id} className="col-md-4">
                                        <article className="card h-100 border-0 shadow-sm">
                                            <img
                                                src={relatedPost.image_url}
                                                alt={relatedPost.title}
                                                className="card-img-top"
                                                style={{ height: '200px', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.style.backgroundColor = '#e9ecef';
                                                    e.target.style.display = 'flex';
                                                    e.target.style.alignItems = 'center';
                                                    e.target.style.justifyContent = 'center';
                                                    e.target.innerHTML = '<i class="fas fa-image text-muted fa-2x"></i>';
                                                }}
                                            />
                                            <div className="card-body">
                                                <h3 className="card-title h6">
                                                    <Link
                                                        to={`/blog/${relatedPost.slug}`}
                                                        className="text-decoration-none text-dark"
                                                    >
                                                        {relatedPost.title}
                                                    </Link>
                                                </h3>
                                                <p className="card-text text-muted small">
                                                    {relatedPost.excerpt && relatedPost.excerpt.length > 100
                                                        ? relatedPost.excerpt.substring(0, 100) + '...'
                                                        : relatedPost.excerpt
                                                    }
                                                </p>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <time className="text-muted small" dateTime={relatedPost.published_date}>
                                                        {formatDate(relatedPost.published_date)}
                                                    </time>
                                                    <small className="text-muted">
                                                        <i className="far fa-clock me-1"></i>
                                                        {relatedPost.reading_time} dk
                                                    </small>
                                                </div>
                                            </div>
                                        </article>
                                    </div>
                                ))}
                            </div>
                        </aside>
                    )}
                </main>
            </div>
        </>
    );
};

export default BlogDetail; 